import type { RecordMedication } from '@adhd/shared';

/**
 * What was taken on the day of a record.
 *
 * Context, not explanation. The wording avoids any suggestion that the
 * medication produced the moment — someone reading their own record back at 2am
 * does not need the app implying a causal story it cannot support, and a
 * clinician does not need one implied either. It says what was logged and stops.
 *
 * Only logged doses appear. A dose the app assumed from silence is left out
 * entirely; beside an account of a bad afternoon, the difference between
 * recorded and inferred is the whole difference.
 */
export function RecordMeds({
  medications,
  /** The owner's zone. Omitted only where reader and owner are the same person. */
  timezone,
}: {
  medications: RecordMedication[];
  timezone?: string | null;
}): JSX.Element | null {
  if (medications.length === 0) return null;

  return (
    <div className="rec-meds">
      <span className="rec-meds-label">That day</span>
      <ul>
        {medications.map((m, i) => (
          <li key={`${m.name}-${i}`}>
            <strong>{m.name}</strong>
            {m.doseAmount !== null && ` ${m.doseAmount}${m.doseUnit === 'other' ? '' : ` ${m.doseUnit}`}`}
            {' · '}
            {describeStatus(m, timezone)}
            {m.feelings.length > 0 && (
              <span className="rec-meds-feel"> — felt {m.feelings.join(', ')}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The status, named outright.
 *
 * "not taken" is ambiguous where it matters most — beside a record a prescriber
 * may read, it could mean forgotten or deliberately held back, and those are
 * different clinical facts. The app already knows which; it just was not saying.
 * So the word is the status: Taken, Missed, Skipped.
 *
 * `late` is a dose that WAS taken and must never read as a failure. It says so
 * and keeps the time, because when it was taken is the interesting part.
 */
function describeStatus(m: RecordMedication, timezone?: string | null): string {
  const time = m.at
    ? new Date(m.at).toLocaleTimeString(undefined, {
        hour: 'numeric', minute: '2-digit', ...(timezone ? { timeZone: timezone } : {}),
      })
    : null;
  switch (m.status) {
    case 'missed':
      return 'Missed';
    case 'skipped':
      return 'Skipped';
    case 'late':
      return time ? `Taken late, ${time}` : 'Taken late';
    default:
      return time ? `Taken ${time}` : 'Taken';
  }
}
